import { useFeatureFlags } from "@/core/auth/hooks";
import { useListCamerasQuery } from "@/core/cameraApi";
import type { SahiConfig } from "@/core/cameraApi/schemas/nn";
import { DEFAULT_NN_RUNTIME_CONFIG } from "@/core/cameraApi/schemas/nn";
import { useCameraApiUrl } from "@/hooks";
import { useSelectedCameraStream } from "@/modules/camera-selection";
import { DashboardPage } from "@/modules/dashboard";
import {
  useGetDashboardConfigQuery,
  useGetDashboardConfigsQuery,
} from "@/modules/dashboard/services/dashboardConfigApi";
import { useActiveProject } from "@/modules/project/hooks/useActiveProject";
import { ReportsPage } from "@/modules/reports";
import {
  ConfigurationTab,
  RunSubheader,
  RunTab,
  type ConfigurationTabHandle,
} from "@/modules/run";
import { Button } from "@/modules/shadcn/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/modules/shadcn/ui/dialog";
import { TriangleAlert } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useBlocker } from "react-router";
import { toast } from "sonner";
import type { ConfigSelection } from "../../hooks/useRunDeploy";
import { useRunDeploy } from "../../hooks/useRunDeploy";
import { DeployConfigSelector } from "../DeployConfigSelector/DeployConfigSelector";

export const RunPage = () => {
  const { t } = useTranslation(["run", "common"]);
  const [activeTab, setActiveTab] = useState<RunTab>("inference");
  const [isReportExporting, setIsReportExporting] = useState(false);
  const [activeConfigId, setActiveConfigId] = useState<number | null>(null);
  const [selectedConfigs, setSelectedConfigs] = useState<ConfigSelection[]>([]);
  const [sahiConfig, setSahiConfig] = useState<SahiConfig | null>(null);
  // The Inference tab's model picker while it is mounted: it can hold an
  // unsaved pick that beforeDeploy is about to persist.
  const [draftModelId, setDraftModelId] = useState<string | null>();
  const configTabRef = useRef<ConfigurationTabHandle>(null);

  const [activeProject] = useActiveProject();
  const projectId = activeProject?.id;
  const cameraApiUrl = useCameraApiUrl();
  const { reporting, restricted } = useFeatureFlags();

  // Fetch configs list so we can auto-select on mount (regardless of active tab)
  const { data: configs = [] } = useGetDashboardConfigsQuery(
    { projectId: projectId! },
    { skip: !projectId },
  );

  useEffect(() => {
    if (activeConfigId === null && configs.length > 0) {
      setActiveConfigId(configs[0].id);
    }
  }, [configs, activeConfigId]);

  // Skipped until the camera is addressable — on a reload straight onto /run
  // the relay session cookie is still being minted, and an ungated query would
  // fire against no base URL at all.
  const { data: cameras, isLoading: camerasLoading } = useListCamerasQuery(
    undefined,
    { skip: !cameraApiUrl },
  );

  // Unified selection across Capture and Run — see useSelectedCameraStream.
  // The camera is the project's DB setting; the stream is session state
  // shared with Capture.
  const { cameraMxid: selectedCamera, streamName: selectedStream } =
    useSelectedCameraStream();

  const { data: dashboardConfig } = useGetDashboardConfigQuery(
    { projectId: projectId!, configId: activeConfigId! },
    { skip: !projectId || !activeConfigId },
  );

  const selectedCameraInfo = cameras?.find((c) => c.mxid === selectedCamera);

  const hasModel =
    draftModelId !== undefined
      ? draftModelId !== null
      : dashboardConfig?.modelId != null;

  const {
    handleDeploy,
    handleStop,
    isDeploying,
    deployPhase,
    deployProgress,
    dashboardUrl,
    canDeploy,
    showDeployConfirm,
    handleConfirmDeploy,
    handleCancelDeploy,
  } = useRunDeploy({
    selectedCamera,
    selectedStream,
    selectedCameraInfo,
    activeConfigId,
    activeProjectId: projectId ?? null,
    hasModel,
    capturedVideoId: dashboardConfig?.capturedVideoId ?? null,
    cameraApiUrl,
    selectedConfigs,
    sahiConfig,
    runtimeConfig: DEFAULT_NN_RUNTIME_CONFIG,
    beforeDeploy: async () => {
      return await configTabRef.current?.saveIfDirty();
    },
  });

  // Dead weight without a Deploy button — it only feeds the deploy payload.
  const configSelector =
    projectId && !restricted ? (
      <DeployConfigSelector
        activeProjectId={projectId}
        activeConfigId={activeConfigId}
        selectedConfigs={selectedConfigs}
        onSelectionChange={setSelectedConfigs}
      />
    ) : undefined;

  // Block in-app navigation while a deploy is in flight (bouncing the user
  // away mid-deploy can leave the camera in an inconsistent state) or while a
  // report export is generating (ReportsPage unmounting drops the download).
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      (isDeploying || isReportExporting) &&
      currentLocation.pathname !== nextLocation.pathname,
  );

  useEffect(() => {
    if (blocker.state === "blocked") {
      toast.warning(
        isDeploying ? t("page.deployInProgress") : t("page.exportInProgress"),
      );
      blocker.reset?.();
    }
  }, [blocker, isDeploying, t]);

  // Leaving the page is only destructive while the browser is the one doing
  // the work. Once the camera has accepted the deployment and is fetching the
  // models itself, closing the tab costs nothing — the deploy completes
  // regardless — so warning then would be a lie.
  const isCameraPulling = deployPhase === "downloading-model" && !!deployProgress;
  const blockUnload = (isDeploying && !isCameraPulling) || isReportExporting;

  useEffect(() => {
    if (!blockUnload) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [blockUnload]);

  // A live flag flip would otherwise leave Tabs pointing at a trigger the
  // subheader no longer renders.
  useEffect(() => {
    if (!reporting && activeTab === "reports") {
      setActiveTab("inference");
    }
  }, [reporting, activeTab]);

  // The tabs are local state, invisible to the router blocker above — gate
  // them here so an in-flight export can't lose its Reports tab.
  const handleTabChange = useCallback(
    (tab: RunTab) => {
      if (isReportExporting && tab !== "reports") {
        toast.warning(t("page.exportInProgress"));
        return;
      }
      setActiveTab(tab);
    },
    [isReportExporting, t],
  );

  const handleConfigChange = useCallback((configId: number | null) => {
    setActiveConfigId(configId);
  }, []);

  const hasCameras = !!cameras && cameras.length > 0;
  const cameraReady = !camerasLoading && hasCameras;

  // Inference now owns the configuration controls (camera/sensor pickers,
  // model, zone, replay video) — the page falls back to a dataset preview
  // image when no camera/stream is selected, so it stays accessible without
  // a live device. Dashboard contains test-case and evaluation sub-tabs.
  const renderTabContent = () => {
    if (activeTab === "reports" && reporting) {
      return (
        <ReportsPage
          dashboardId={activeConfigId}
          projectId={projectId ?? null}
          onExportingChange={setIsReportExporting}
        />
      );
    }

    if (activeTab === "dashboard") {
      return (
        <DashboardPage
          dashboardUrl={dashboardUrl}
          onConfigChange={handleConfigChange}
        />
      );
    }

    return projectId ? (
      <ConfigurationTab
        ref={configTabRef}
        projectId={projectId}
        configId={activeConfigId}
        sahiConfig={sahiConfig}
        onSahiConfigChange={setSahiConfig}
        onModelSelectionChange={setDraftModelId}
      />
    ) : null;
  };

  return (
    <div className="-m-6 flex min-h-0 flex-1 flex-col bg-white">
      <RunSubheader
        activeTab={activeTab}
        onTabChange={handleTabChange}
        onDeploy={handleDeploy}
        onStop={handleStop}
        deployPhase={deployPhase}
        deployProgress={deployProgress}
        canDeploy={cameraReady && canDeploy}
        hasModel={hasModel}
        isDeployed={!!dashboardUrl}
        configSelector={configSelector}
      />
      <div className="flex min-h-0 flex-1 flex-col overflow-auto">
        {renderTabContent()}
      </div>

      <DeployConfirmDialog
        open={showDeployConfirm}
        onConfirm={handleConfirmDeploy}
        onCancel={handleCancelDeploy}
      />
    </div>
  );
};

const DeployConfirmDialog = ({
  open,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) => {
  const { t } = useTranslation(["run", "common"]);
  return (
    <Dialog open={open} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent showCloseButton={false}>
        <DialogHeader>
          <div className="flex flex-row items-center gap-2">
            <TriangleAlert className="size-5 text-amber-500" />
            <DialogTitle>{t("deployConfirm.title")}</DialogTitle>
          </div>
          <DialogDescription>{t("deployConfirm.description")}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            {t("common:actions.cancel")}
          </Button>
          <Button onClick={onConfirm}>{t("deployConfirm.confirm")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
